import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  linkTextClasses,
  Page,
  PageHeader,
  TextArea,
} from '@evogard/ui';
import type {
  BillingChannel,
  Discard,
  PipelineStage,
  DiscardReason,
  PipelinePause,
  PipelinePreview,
  LastIntake,
} from '@evogard/contract';
import { count, loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
/*
 * O vocabulário do módulo vem de `common.tsx` e não é reescrito aqui: o rótulo
 * do canal, a lista do que a casa realmente envia, o corte dos segundos da
 * janela, a limitação de fuso e a frase sobre de onde a régua vem aparecem
 * também na mesa e na tela da régua. Escritos de novo nesta tela, eles divergem
 * na primeira correção — e o sintoma é a esteira dizendo que o WhatsApp sai
 * enquanto a régua ao lado diz que não.
 */
import {
  Age,
  TIMEZONE_NOTICE,
  LADDER_SOURCE,
  CHANNEL_LABEL,
  trimSeconds,
  channelDispatches,
} from './common.js';

/**
 * A esteira de cobrança da base atual — `cobranca-25`.
 *
 * ## O assunto desta tela é o DESCARTE, não o envio
 *
 * Hoje ninguém responde "por que este associado não recebeu a cobrança?" sem
 * abrir o log de dois sistemas que não se falam. A prévia responde: por etapa,
 * quantos passariam e quantos não passariam, **com o motivo de cada um**. Os
 * motivos são vocabulário fechado (`DISPATCH_DISCARD_REASONS`) e vêm de
 * `canDispatch`, a função pura que o job de disparo também chama — esta tela
 * nomeia cada valor e não reimplementa nenhuma decisão. Um `switch` de regra
 * aqui seria a terceira opinião sobre quem recebe cobrança, que é exatamente o
 * defeito que o módulo desfaz.
 *
 * ## As quatro coisas que ela se recusa a afirmar
 *
 * 1. **"Sem régua" não é "hoje não há nada a enviar".** A união discriminada do
 *    contrato existe para isso, e é o único caminho que roda hoje:
 *    `billing_tiers` nasce vazia nas duas praças. Sem faixa não há etapa,
 *    canal nem janela — a esteira não dispara nada, e o conserto é a carga da
 *    régua legada, não esperar o dia passar.
 * 2. **"Enviável" não é "vai chegar".** `canDispatch` não pergunta se existe
 *    endereço para o canal; o serviço declara isso e não conserta, porque o
 *    sexto motivo nasceria em dois arquivos ao mesmo tempo. Então a tela conta o
 *    que o servidor mede e diz, com todas as letras, o que a conta não mede.
 * 3. **O número não é de agora.** Ele é o retrato da última coleta, e a tela diz
 *    há quanto tempo ela foi — "nunca coletou" tem estado próprio, porque
 *    `boletos: 0` diria a mesma coisa que "coletamos e não achamos nada".
 * 4. **A janela não é a hora da praça.** É avaliada no fuso da casa, porque o
 *    fuso do tenant não existe na plataforma. Escrever "das 8h às 18h, horário
 *    da praça" seria a tela afirmando o que o código não cumpre.
 *
 * ## O que ela não tem
 *
 * Nenhum botão de "disparar agora". Quem dispara é o job de hora em hora, e um
 * botão aqui precisaria de rota que o contrato não declara. Nenhum seletor de
 * praça: a resposta é da base da sessão, e a permissão é por grupo.
 */
export function PipelineScreen() {
  const { data: context } = useSessionContext();
  const query = usePipeline();
  const canPause = can(context, 'billing.pipeline', 'pausar');

  const pipeline = query.data;

  return (
    <Page measure="cheia">
      <PageHeader title="Esteira de cobrança">
        O que a cobrança automática faria agora na base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        A régua, a pausa e o estoque são desta praça — trocar de base troca a resposta
        inteira.
      </PageHeader>

      {query.isError && (
        <Alert tone="erro" title="Não foi possível ler o estado da esteira.">
          {query.error.message}
        </Alert>
      )}

      <div className="grid gap-5 sm:grid-cols-2">
        <PauseCard pauseState={pipeline?.pause} canPause={canPause} />
        <IntakeCard intake={pipeline?.intake} />
      </div>

      {/*
       * Erro sem dado nenhum: o aviso lá em cima é a resposta inteira. Desenhar
       * a prévia abaixo dele anunciaria "esta base não tem régua" ou "nenhum
       * descarte hoje" — duas afirmações sobre o mundo feitas quando a casa não
       * sabe nada sobre ele.
       */}
      {(!query.isError || pipeline) && (
        <PreviewSection
          preview={pipeline?.preview}
          intake={pipeline?.intake}
          loading={query.isPending}
        />
      )}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * A consulta
 * ------------------------------------------------------------------------- */

/**
 * O estado da esteira da base atual.
 *
 * `tenantId` na chave e `placeholderData` preso à mesma base: a régua, a pausa e
 * o estoque são de uma praça só, e segurar o resultado anterior durante a troca
 * mostraria a prévia da CE sob o nome da GO — nesta tela, cujo cabeçalho nomeia
 * a base, é o defeito mais provável de todos.
 *
 * A consulta mora aqui e não num `common.tsx` do módulo porque hoje só esta tela
 * a faz. Quando a segunda precisar, ela sobe — junto com a chave, para as duas
 * lerem o mesmo cache em vez de baterem duas vezes na consulta que monta o
 * ensaio da praça inteira.
 */
function usePipeline() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['cobranca', 'esteira', tenantId],
    queryFn: async () => {
      const r = await api.billing.pipeline();
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a esteira desta base'));
      }
      return r.body;
    },
    placeholderData: (previous, queryPrevious) =>
      queryPrevious?.queryKey[2] === tenantId ? previous : undefined,
  });
}

/* ------------------------------------------------------------------------- *
 * A pausa
 * ------------------------------------------------------------------------- */

/**
 * O estado da pausa, e o único ato que esta tela oferece.
 *
 * ## Pausada mostra DESDE QUANDO, POR QUEM e o MOTIVO — os três
 *
 * É para isso que `app.billing_pauses` existe. Uma pausa sem motivo é uma
 * cobrança parada que ninguém sabe se pode retomar, e o padrão de quem não sabe
 * é deixar como está — a praça fica meses sem cobrar e ninguém consegue apontar
 * a decisão. `por` nulo é "foi o sistema", e não "não sabemos".
 *
 * ## E pausada NÃO oferece "pausar"
 *
 * O servidor responde a um segundo pedido de pausa devolvendo a pausa que já
 * existe, sem escrever: o motivo e o autor de quem chegou primeiro ficam de pé.
 * Oferecer o botão assim mesmo faria a tela convidar para um ato que não
 * acontece — a pessoa escreveria o próprio motivo, mandaria, e leria de volta o
 * motivo de outra pessoa sem entender por quê.
 */
function PauseCard({
  pauseState,
  canPause,
}: {
  pauseState: PipelinePause | undefined;
  canPause: boolean;
}) {
  return (
    <Card title="Cobrança automática">
      {!pauseState ? (
        <div className="flex flex-col gap-2">
          <span className="esqueleto block h-6 w-40" />
          <span className="esqueleto block h-4 w-full" />
        </div>
      ) : pauseState.estado === 'pausada' ? (
        <div className="flex flex-col gap-3">
          <Badge tone="alerta">Pausada</Badge>
          <p className="text-sm text-texto-suave">
            Nada sai desta base enquanto ela estiver assim — nem o que a prévia abaixo
            conta como enviável. A pausa não tem prazo: ela só termina quando alguém
            retomar aqui.
          </p>
          <dl className="flex flex-col gap-1.5 text-sm">
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-texto-suave">Desde:</dt>
              <dd>
                <Age iso={pauseState.since} />
              </dd>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <dt className="text-texto-suave">Por:</dt>
              <dd>
                {/* nulo é "foi o sistema", e o contrato declara essa
                    possibilidade — não é "não sabemos quem" */}
                {pauseState.by ?? <span className="text-texto-suave">o sistema</span>}
              </dd>
            </div>
            <div>
              <dt className="rotulo text-texto-suave">Motivo</dt>
              <dd className="mt-0.5 whitespace-pre-wrap">{pauseState.reason}</dd>
            </div>
          </dl>
          {canPause ? (
            <Resume />
          ) : (
            <p className="text-xs text-texto-suave">
              Você vê o estado da esteira e não pode retomá-la. Quem administra a
              plataforma concede a ação <strong className="font-medium">pausar</strong> em{' '}
              <strong className="font-medium">cobranca.esteira</strong>, em Grupos — a
              mesma concessão pausa e retoma.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/*
           * "Não pausada", e não "Ativa" em verde.
           *
           * O verde da casa quer dizer "está certo, deu certo". Sem régua a
           * esteira não dispara nada, e um selo verde ao lado disso diria que a
           * cobrança automática está funcionando quando ela está apenas
           * destravada. O que este selo afirma é só o que a tabela de pausas
           * sabe: ninguém a parou.
           */}
          <Badge tone="informacao">Não pausada</Badge>
          <p className="text-sm text-texto-suave">
            Ninguém parou a cobrança automática desta base. Isso não quer dizer que ela vá
            disparar alguma coisa: o que ela faria agora é a prévia abaixo.
          </p>
          {canPause && <PauseForm />}
        </div>
      )}
    </Card>
  );
}

/** Pausar e retomar são a mesma rota e a mesma permissão — ver o contrato. */
function usePause() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (
      act: { action: 'pausar'; reason: string } | { action: 'retomar' },
    ) => {
      const r = await api.billing.pausePipeline({ body: act });
      return ok(r, 200, 'Não foi possível alterar a pausa.');
    },
    /*
     * Invalida o módulo inteiro: a pausa muda o que o painel do início e a mesa
     * dizem sobre esta base, e não só este cartão.
     */
    onSuccess: () => client.invalidateQueries({ queryKey: ['cobranca'] }),
  });
}

function Resume() {
  const pause = usePause();

  return (
    <div className="flex flex-col items-start gap-1">
      {pause.isError && (
        <span role="alert" className="text-xs font-medium text-erro">
          {pause.error.message}
        </span>
      )}
      <Button
        loading={pause.isPending}
        onClick={() => pause.mutate({ action: 'retomar' })}
      >
        Retomar a cobrança automática
      </Button>
    </div>
  );
}

/**
 * Pausar, em dois passos e sem diálogo.
 *
 * O motivo é obrigatório no contrato (3 a 300 caracteres), então o campo tem de
 * existir antes do envio — mas ele não fica aberto o tempo todo: um formulário
 * permanente ao lado de "não pausada" faria parecer que pausar é uma
 * configuração da tela. O passo a mais é a confirmação, e ele é inline e
 * progressivo, não uma camada por cima da página.
 *
 * `perigo` e não `primario`: o ato para de cobrar a praça inteira por tempo
 * indeterminado. É a mesma variante que a casa usa para o que não se desfaz
 * sozinho.
 */
function PauseForm() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const pause = usePause();

  if (!open) {
    return (
      <div>
        <Button variant="perigo" onClick={() => setOpen(true)}>
          Pausar a cobrança automática
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        pause.mutate(
          { action: 'pausar', reason: reason.trim() },
          { onSuccess: () => setOpen(false) },
        );
      }}
    >
      {pause.isError && <Alert tone="erro">{pause.error.message}</Alert>}

      <Alert tone="alerta" title="Isto para a cobrança desta praça inteira.">
        Nenhum e-mail de cobrança sai desta base enquanto a pausa durar, e ela não tem
        prazo de fim — volta quando alguém retomar por aqui. A outra praça não é afetada.
      </Alert>

      <TextArea
        label="Por que está pausando"
        required
        minLength={3}
        maxLength={300}
        rows={3}
        counter
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        help="Quem chegar depois lê isto para decidir se pode retomar. É o que a tabela de pausas existe para guardar."
      />

      <div className="flex gap-2">
        <Button
          type="submit"
          variant="perigo"
          loading={pause.isPending}
          disabled={reason.trim().length < 3}
        >
          Pausar
        </Button>
        <Button
          variant="discreto"
          onClick={() => {
            setOpen(false);
            setReason('');
          }}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * A coleta
 * ------------------------------------------------------------------------- */

/**
 * A última coleta — e por que "nunca" tem desenho próprio.
 *
 * A coleta monta `boletos` a partir do espelho do ERP, e o espelho está
 * construído e vazio nas duas praças. "Nunca rodou aqui" e "rodou e não achou
 * boleto nenhum" produzem a mesma tela se o campo for um número — e as duas
 * pedem ações opostas: a primeira é problema nosso, a segunda é uma resposta
 * sobre o estoque.
 */
function IntakeCard({ intake }: { intake: LastIntake | undefined }) {
  return (
    <Card title="Estoque coletado">
      {!intake ? (
        <div className="flex flex-col gap-2">
          <span className="esqueleto block h-7 w-24" />
          <span className="esqueleto block h-4 w-full" />
        </div>
      ) : intake.estado === 'nunca' ? (
        <div className="flex flex-col gap-2">
          <Badge tone="neutro">Nunca coletou</Badge>
          <p className="text-sm text-texto-suave">
            A coleta nunca rodou nesta base. É ela que monta o estoque de boletos a partir
            do espelho do ERP — sem estoque não há o que a esteira olhe, e todo número da
            prévia abaixo é zero por falta de dado, não por falta de dívida.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xl font-bold tabular-nums">
            {intake.boletos}
            <span className="text-sm font-normal text-texto-suave">
              {' '}
              {intake.boletos === 1 ? 'boleto' : 'boletos'}
            </span>
          </p>
          <p className="text-sm text-texto-suave">
            Coletados <Age iso={intake.at} />. Os números desta tela são o retrato daquele
            instante — não o do ERP agora.
          </p>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * A prévia
 * ------------------------------------------------------------------------- */

/**
 * O que a esteira faria agora, por etapa.
 *
 * Os dois ramos da união do contrato são telas diferentes de propósito, e o
 * primeiro é o único que roda hoje.
 */
function PreviewSection({
  preview,
  intake,
  loading,
}: {
  preview: PipelinePreview | undefined;
  intake: LastIntake | undefined;
  loading: boolean;
}) {
  const withoutInventory = intake?.estado === 'nunca';

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">Prévia de agora</h2>
        <p className="mt-0.5 text-sm text-texto-suave">
          O ensaio é o mesmo código que o job de disparo executa — a diferença entre os
          dois é que um envia e o outro não.
        </p>
      </div>

      {loading && !preview ? (
        <div className="flex flex-col gap-3">
          <span className="esqueleto block h-24 w-full rounded-borda" />
          <span className="esqueleto block h-24 w-full rounded-borda" />
        </div>
      ) : !preview ? null : preview.estado === 'sem_regua' ? (
        <WithoutLadder
          ladderOutside={preview.ladderOutside}
          withoutInventory={withoutInventory}
        />
      ) : (
        <LadderBuilt preview={preview} withoutInventory={withoutInventory} />
      )}

      {preview && <OQueEntraNaAccount withoutInventory={withoutInventory} />}
    </section>
  );
}

/**
 * O estado que roda hoje nas duas praças.
 *
 * A frase não pode ser "nada a enviar hoje". Uma praça sem régua e uma praça em
 * dia produzem a mesma tela numa lista lisa, e a leitura tranquilizadora é a
 * errada: aqui ninguém configurou nada, todo boleto está fora da régua, e a
 * esteira ficaria assim para sempre sem erro em lugar nenhum.
 */
function WithoutLadder({
  ladderOutside,
  withoutInventory,
}: {
  ladderOutside: number;
  withoutInventory: boolean;
}) {
  return (
    <Alert tone="alerta" title="Esta base não tem régua de cobrança.">
      <p className="mt-1">
        Não é “hoje não há nada a enviar”: é que não existe faixa nenhuma cadastrada aqui.
        Sem faixa não há etapa, não há canal e não há janela —{' '}
        <strong className="font-medium">a esteira não dispara nada</strong>, e continuaria
        assim indefinidamente, sem erro e sem registro.
      </p>
      <p className="mt-2">
        {withoutInventory ? (
          <>
            Nenhum boleto está fora da régua porque nenhum boleto foi coletado ainda. Os
            dois destravam em ordem: a coleta primeiro, a régua depois.
          </>
        ) : (
          <>
            <strong className="font-medium tabular-nums">{ladderOutside}</strong>{' '}
            {ladderOutside === 1 ? 'boleto em aberto está' : 'boletos em aberto estão'}{' '}
            fora da régua — {ladderOutside === 1 ? 'ele' : 'todos'}, porque não há faixa
            em que {ladderOutside === 1 ? 'caiba' : 'caibam'}.
          </>
        )}
      </p>
      <p className="mt-2">{LADDER_SOURCE}</p>
      <p className="mt-2">
        O que existe nesta base se vê em{' '}
        <Link to="/billing/tiers" className={linkTextClasses()}>
          Régua de cobrança
        </Link>
      </p>
    </Alert>
  );
}

function LadderBuilt({
  preview,
  withoutInventory,
}: {
  preview: Extract<PipelinePreview, { estado: 'montada' }>;
  withoutInventory: boolean;
}) {
  /*
   * A nota sobre o fuso só aparece se alguma etapa tiver janela declarada. Dita
   * sobre uma régua em que todas as janelas são nulas, ela explicaria uma
   * limitação de um campo que ninguém preencheu.
   */
  const hasWindow = preview.byStep.some((e) => e.windowStart && e.windowEnd);

  return (
    <div className="flex flex-col gap-4">
      <Alert
        tone="informacao"
        title="“Enviável” quer dizer que passou nas verificações — não que vai chegar."
      >
        <p className="mt-1">
          A conta pergunta cinco coisas: se o contato está bloqueado, se há atendimento
          com operador, se aquele contato já foi feito nesta etapa, se o atraso cai na
          régua e se agora está dentro da janela. Ela{' '}
          <strong className="font-medium">não pergunta se existe endereço</strong> para o
          canal — um boleto cujo associado não tem e-mail no espelho conta como enviável
          aqui e falha no envio por falta de destinatário.
        </p>
      </Alert>

      {preview.byStep.map((step) => (
        <StepCard key={step.step} step={step} />
      ))}

      <Card title="Fora da régua" level={3}>
        <p className="text-xl font-bold tabular-nums">{preview.ladderOutside}</p>
        <p className="mt-1 text-sm text-texto-suave">
          {withoutInventory
            ? 'Zero porque não há estoque coletado nesta base, e não porque todo mundo cabe em alguma faixa.'
            : 'Boletos em aberto cujo atraso não cai em faixa nenhuma desta régua. Eles não recebem cobrança automática — nem hoje nem depois, enquanto a régua for esta. É o inadimplente que a esteira de hoje nunca alcança, e vê-lo aqui é decisão consciente em vez de herança por acidente.'}
        </p>
      </Card>

      {hasWindow && <p className="text-xs text-texto-suave">{TIMEZONE_NOTICE}</p>}
    </div>
  );
}

/**
 * Uma etapa: o que sai, o que não sai e por quê.
 *
 * Cartão por etapa, e não linha de tabela, porque a lista de descartes é o
 * assunto — espremida numa célula ela vira a coluna que ninguém lê, que é o
 * estado atual do problema (o motivo existe, no log de dois sistemas, e ninguém
 * abre).
 *
 * O título é a `etapa` crua, porque **ela é o nome que existe**: a etapa é a
 * identidade da faixa, é o que `cobranca_contatos.etapa` guarda, e o contrato
 * não traz rótulo separado nenhum. Embelezá-la aqui — trocar `_` por espaço e
 * capitalizar — publicaria um nome de etapa que a base nunca escreveu, que é a
 * mesma recusa que `phaseLabel` faz na retenção.
 *
 * `ordem` aparece como número e não como "3ª faixa": ela é chave de ordenação,
 * não posição na lista — a régua legada pode numerar de dez em dez, e o ordinal
 * inventado seria a tela afirmando uma contagem que ninguém fez.
 */
function StepCard({ step }: { step: PipelineStage }) {
  const discarded = step.discards.reduce((t, d) => t + d.count, 0);

  return (
    <Card
      title={step.step}
      descricao={`Ordem ${step.order} na régua desta base`}
      level={3}
    >
      <dl className="grid gap-4 sm:grid-cols-3">
        <div>
          <dt className="rotulo text-texto-suave">Enviáveis agora</dt>
          <dd className="mt-0.5 text-xl font-bold tabular-nums">{step.sendable}</dd>
        </div>
        <div>
          <dt className="rotulo text-texto-suave">Canais da faixa</dt>
          <dd className="mt-0.5 text-sm">
            <TierChannels channels={step.channels} />
          </dd>
        </div>
        <div>
          <dt className="rotulo text-texto-suave">Janela</dt>
          <dd className="mt-0.5 text-sm">
            <TierWindow start={step.windowStart} end={step.windowEnd} />
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-borda pt-3">
        <h4 className="rotulo text-texto-suave">
          Descartados agora{' '}
          {discarded > 0 && <span className="tabular-nums">({discarded})</span>}
        </h4>
        <Discards discards={step.discards} sendable={step.sendable} />
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * Canal, janela e motivo
 * ------------------------------------------------------------------------- */

/**
 * Os canais da faixa — e a confissão de que declarar não é enviar.
 *
 * `common/notifications` manda e-mail, e só. WhatsApp precisa poder ser gravado
 * numa faixa, senão a régua extraída da esteira legada nasceria diferente da que
 * está no ar; mas gravar não é enviar, e sem esta frase a prévia contaria como
 * enviável o que ninguém envia e a esteira pareceria trabalhar.
 */
function TierChannels({ channels }: { channels: BillingChannel[] }) {
  if (channels.length === 0) {
    return (
      <span className="text-texto-suave">
        Nenhum — esta faixa não envia por canal nenhum.
      </span>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {channels.map((channel) => (
        <li key={channel}>
          {CHANNEL_LABEL[channel]}
          {!channelDispatches(channel) && (
            <span className="block text-xs font-medium text-alerta">
              A plataforma ainda não envia por este canal. A faixa o declara e nada sai
              por ele.
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * A janela — e o nulo que NÃO é "a qualquer hora".
 *
 * Janela nula é "ninguém declarou quando esta etapa pode falar com o associado",
 * e quem decide o que fazer com isso é o servidor: ele não envia. Desenhar "—"
 * aqui apagaria a diferença entre uma etapa fora do horário agora e uma etapa
 * que nunca vai enviar nada.
 */
function TierWindow({ start, end }: { start: string | null; end: string | null }) {
  if (start === null || end === null) {
    return (
      <span className="font-medium text-alerta">
        Sem janela declarada — esta etapa não envia
      </span>
    );
  }
  return (
    <span className="tabular-nums">
      das {trimSeconds(start)} às {trimSeconds(end)}
    </span>
  );
}

/**
 * Por que cada boleto não sai — a resposta que hoje mora no log de dois
 * sistemas.
 *
 * `Record` completo sobre `DiscardReason`: motivo novo no domínio e no
 * contrato não compila até ganhar frase aqui. Um motivo sem frase apareceria na
 * tela como `contato_ja_enviado`, que é o vocabulário do código vazando para
 * quem opera.
 *
 * As frases dizem o fato, não o conserto: "contato bloqueado" é uma decisão do
 * associado que a casa cumpre, e não um problema a resolver.
 */
// @valor
const LABEL_DESCARTE: Record<DiscardReason, string> = {
  contato_bloqueado: 'O contato pediu para não ser contatado',
  atendimento_com_operador: 'Há atendimento com operador — alguém está negociando',
  contato_ja_enviado: 'Este contato já foi feito nesta etapa',
  fora_da_regua: 'O atraso não cai em nenhuma faixa desta régua',
  fora_da_janela: 'Fora da janela de horário da faixa',
};

function Discards({ discards, sendable }: { discards: Discard[]; sendable: number }) {
  if (discards.length === 0) {
    /*
     * Duas ausências diferentes, e a distinção manda a pessoa para lugares
     * diferentes: sem descarte E sem enviável é uma faixa que não pegou
     * ninguém; sem descarte com enviáveis é uma faixa em que todo mundo passou.
     */
    return (
      <p className="mt-1 text-sm text-texto-suave">
        {sendable === 0
          ? 'Nenhum boleto está nesta faixa agora — não há o que enviar nem o que descartar.'
          : `Nenhum descarte: ${count(sendable, 'boleto passou', 'boletos passaram')} pelas cinco verificações.`}
      </p>
    );
  }

  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {discards.map((d) => (
        <li key={d.reason} className="flex items-baseline justify-between gap-3 text-sm">
          <span className="min-w-0">{LABEL_DESCARTE[d.reason]}</span>
          <span className="shrink-0 font-medium tabular-nums">{d.count}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * O que entra nestas contas — a nota de rodapé que impede a leitura otimista.
 *
 * Só o boleto **em aberto** entra: situação classificada, não pago e não
 * cancelado. `app.erp_status_classifications` nasce vazia, e enquanto estiver
 * assim nenhum código de situação é classificado — a conta é zero em toda praça
 * mesmo com estoque coletado. Sem esta frase, "0 fora da régua" ao lado de
 * milhares de boletos leria como trabalho terminado.
 *
 * A tela **não afirma** que a classificação está vazia: ela não lê essa
 * resposta. Diz a regra, e aponta onde conferir.
 */
function OQueEntraNaAccount({ withoutInventory }: { withoutInventory: boolean }) {
  return (
    <p className="text-xs text-texto-suave">
      Só entra nestas contas o boleto em aberto: situação classificada, não pago e não
      cancelado. Boleto cujo código de situação esta base ainda não classificou fica de
      fora —{' '}
      <Link to="/erp/classificacoes" className={linkTextClasses()}>
        Classificação de situações
      </Link>{' '}
      mostra o que já foi classificado aqui.
      {withoutInventory &&
        ' E como a coleta nunca rodou nesta base, não há estoque para nenhuma delas medir.'}
    </p>
  );
}
